<template>
  <div class="affiliate-members">
    <div class="d-flex justify-content-between mb-3">
      <div class="d-flex flex-wrap" style="gap: 20px;">
        <div class="d-flex flex-column">
          <span class="font-weight-bold">Bets amount</span>
          <div class="d-flex align-items-center" style="gap: 5px;">
            <a-input-number
              v-model="filters.amount_min"
              type="number"
              size="large"
              placeholder="Min"
              style="min-width: 120px;"
              :disabled="!hasListData"
              :loading="loading"
              @change="changeAmountFilters('amountMinTimeout')"
            />
            <span>-</span>
            <a-input-number
              v-model="filters.amount_max"
              type="number"
              size="large"
              placeholder="Max"
              style="min-width: 120px;"
              :disabled="!hasListData"
              :loading="loading"
              @change="changeAmountFilters('amountMaxTimeout')"
            />
          </div>
        </div>
        <UniversalSelect
          v-model="filters.status"
          style="width: 180px;"
          :options="statusOptions"
          placeholder="Status"
          title="Status"
          :disabled="loading || !hasListData"
          @change="getAffiliateMembersData"
        />
        <div class="d-flex align-items-end" style="height: 62px;">
          <SearchField
            :value="filters.login"
            :loading="loading"
            :disabled="loading || !hasListData"
            @search="changeSearchValue"
          />
        </div>
        <div class="d-flex align-items-end">
          <a-button
            v-if="isFiltered"
            size="large"
            :disabled="loading"
            class="w-fit"
            @click="() => handleReset()"
          >
            Reset filters
          </a-button>
        </div>
      </div>
      <div class="d-flex justify-content-end align-items-end" style="max-height: 62px;">
        <a-button
          type="default"
          size="large"
          class="mr-3"
          :disabled="loading || !haveData"
          @click="toggleShowExportModal()"
        >
          <a-icon type="upload" />
          Export
        </a-button>
        <a-tooltip
          placement="top"
          arrow-point-at-center
          :get-popup-container="getPopupContainer"
        >
          <template v-if="program && isDisabledProgram" slot="title">
            <span>Users cannot be added to the programm with such status</span>
          </template>
          <a-button
            type="primary"
            size="large"
            :disabled="!createPermission || isDisabledProgram"
            @click="toggleShowAddAffiliateModal(true)"
          >
            <a-icon type="plus" /> Add
          </a-button>
        </a-tooltip>
      </div>
    </div>
    <a-table
      :dataSource="data"
      :pagination="meta"
      :columns="columnsComputed"
      :row-key="(record) => record.id"
      :customRow="customRow"
      :loading="loading"
      @change="changePagination"
    >
      <template slot="account" slot-scope="account, record">
        <div class="d-flex align-items-center">
          <CopyValue
            :value="account.login"
            message="Login ID copied to clipboard"
            :copyWithValue="false"
          />
          <div class="custom-link ml-2">
            <router-link :to="`/referral/programs/${programId}/members/${roleSystemName}/${record.id}`">
              {{ account.login }}
            </router-link>
          </div>
        </div>
      </template>
      <template slot="created_at" slot-scope="created_at">
        <span>{{ created_at ? formatDate(created_at) : '-' }}</span>
      </template>
      <template slot="started_at" slot-scope="started_at">
        <span>{{ started_at ? formatDate(started_at) : '-' }}</span>
      </template>
      <template slot="role" slot-scope="role">
        <a-badge
          v-if="role && role?.name"
          :count="role.name"
          style="cursor: default; transform: scale(1.2);"
          :number-style="{
            backgroundColor: role.name === 'Referral' ? '#52c41a' : '#108ee9',
            boxShadow: '0 4px 8px 0 #d9d9d9',
          }"
        />
        <span v-else>—</span>
      </template>
      <template slot="cashback" slot-scope="cashback">
        <div class="d-flex">
          <span v-if="cashback?.sum" class="d-block mr-1">{{ cashback.sum }}</span>
          <span v-if="cashback && typeof cashback.percent === 'number'">({{ cashback.percent }}%)</span>
        </div>
      </template>
      <template slot="status" slot-scope="status">
        <div class="d-flex align-items-center">
          <a-badge
            :count="getStatusName(status)"
            style="cursor: default; transform: scale(1.2);"
            :number-style="{
              backgroundColor: getBackgroundStatus(status),
              boxShadow: '0 4px 8px 0 #d9d9d9',
            }"
          />
        </div>
      </template>
    </a-table>
    <TotalCount :count="meta.total" />
    <AddAffiliateModal
      v-if="showAddAffiliateModal"
      :show="showAddAffiliateModal"
      :roleSystemName="roleSystemName"
      @updateTable="getAffiliateMembersData"
      @close="toggleShowAddAffiliateModal"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import UniversalSelect from '@/components/custom/filters/universalSelect.vue'
import SearchField from '@/components/custom/form/SearchField.vue'
import CopyValue from '@/components/custom/form/CopyValue.vue'
import AddAffiliateModal from '@/views/apps/referral-program/referral-programs/components/AddAffiliateModal.vue'
import TotalCount from '@/views/apps/referral-program/components/TotalCount.vue'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { capitalizeFirstLetter } from '@/composables/useCapitalizeFirstLetter.js'
import { getMembersStatusColors } from '@/views/apps/referral-program/referral-programs/composables/useGetMembersStatusColors.js'
import { getMembersStatuses } from '@/views/apps/referral-program/referral-programs/composables/useGetMembersStatuses.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    role: {
      type: Object,
      default: () => {},
    },
    program: {
      type: Object,
      default: null,
    },
    createPermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    UniversalSelect,
    SearchField,
    CopyValue,
    AddAffiliateModal,
    TotalCount,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    data: [],
    meta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    filters: {
      page: undefined,
      per_page: undefined,
      status: undefined,
      amount_min: undefined,
      amount_max: undefined,
      login: undefined,
      sort_field: undefined,
      sort_dir: undefined,
    },
    columns: [
      {
        title: 'Login',
        dataIndex: 'account',
        width: '15%',
        scopedSlots: { customRender: 'account' },
      },
      {
        title: 'Invitation date',
        dataIndex: 'created_at',
        width: '10%',
        ellipsis: true,
        scopedSlots: { customRender: 'created_at' },
        sorter: true,
        sortOrder: undefined,
      },
      {
        title: 'Start date',
        dataIndex: 'started_at',
        width: '10%',
        ellipsis: true,
        scopedSlots: { customRender: 'started_at' },
        sorter: true,
        sortOrder: undefined,
      },
      {
        title: 'Invited referrals',
        dataIndex: 'children_count',
        width: '15%',
        scopedSlots: { customRender: 'children_count' },
      },
      {
        title: 'Bets amount',
        dataIndex: 'bets_amount',
        width: '15%',
        scopedSlots: { customRender: 'bets_amount' },
      },
      {
        title: 'Cashback',
        dataIndex: 'cashback',
        width: '15%',
        scopedSlots: { customRender: 'cashback' },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        width: '7%',
        scopedSlots: { customRender: 'status' },
      },
    ],
    amountMinTimeout: null,
    amountMaxTimeout: null,
    statusOptions: getMembersStatuses(),
    showAddAffiliateModal: false,
    loading: false,
  }),
  computed: {
    programId() {
      return this.$route.params.id
    },
    roleSystemName() {
      return this.role?.system_name
    },
    isHasListItems() {
      return !!this.data?.length
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '')
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
    isDisabledProgram() {
      if (!this.program) {
        return true
      }

      return !['active', 'inactive'].includes(this.program.status)
    },
    columnsComputed() {
      return this.columns.map((col) => {
        if (col.dataIndex === 'created_at' || col.dataIndex === 'started_at') {
          return {
            ...col,
            sortOrder:
              this.filters.sort_field === col.dataIndex
                ? this.filters.sort_dir === 'asc'
                  ? 'ascend'
                  : 'descend'
                : false,
          }
        }
        return col
      })
    },
    haveData() {
      return !!this.data?.length
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    customRow(member) {
      return {
        on: {
          click: () => {
            this.$router.push(`/referral/programs/${this.programId}/members/${this.roleSystemName}/${member.id}`)
          },
        },
      }
    },
    formatDate(date) {
      return this.$moment(date).tz(this.$moment.tz.guess()).format('DD.MM.YYYY')
    },
    getStatusName(status) {
      return capitalizeFirstLetter(status)
    },
    getBackgroundStatus(status) {
      return getMembersStatusColors(status)
    },
    toggleShowAddAffiliateModal(status = false) {
      this.showAddAffiliateModal = status
    },
    changeAmountFilters(target) {
      clearTimeout(this[target])

      this[target] = setTimeout(() => {
        this.filters.page = 1
        this.getAffiliateMembersData()
        clearTimeout(this[target])
      }, 1000)
    },
    changeSearchValue(value) {
      this.filters.login = value
      this.filters.page = 1
      this.getAffiliateMembersData()
    },
    changePagination(pagination, _, sorter) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize

      if (sorter && sorter?.order) {
        this.filters.sort_field = sorter.field
        this.filters.sort_dir = sorter.order === 'ascend' ? 'asc' : 'desc'
      } else {
        this.filters.sort_field = undefined
        this.filters.sort_dir = undefined
      }

      this.getAffiliateMembersData()
    },
    getFilters() {
      setQueryParams(this.filters, this, 'aff')
      if (!this.isFiltered) {
        return {}
      }

      return getFilledFilters({ filters: this.filters })
    },
    async getAffiliateMembersData() {
      try {
        this.loading = true

        const programId = this.$route.params.id

        const filters = this.getFilters()

        const url = `${referralApiUrl}/referral-programs/${programId}/members/${this.roleSystemName}`
        const response = await apiClient.get(url, { params: filters })
        if (response?.data?.data) {
          this.data = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          Object.assign(this.meta, newMeta)
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    toggleShowExportModal() {
      this.$store.commit('modal/SET_SHOW_MODAL', {
        title: 'Export affiliates',
        filter: {
          type: 'members',
          filter: {
            role: this.role.id,
            ...this.filters,
          },
        },
      })
    },
    handleReset() {
      Object.keys(this.filters).forEach(key => {
        this.filters[key] = undefined
      })
      this.getAffiliateMembersData()
    },
  },
  async mounted() {
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this, 'aff')
    }
    this.getAffiliateMembersData()
  },
}
</script>
